import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { HeicConverterService } from "src/app/shared/services/heic-converter.service";
import { TravelKitService } from "src/app/shared/services/travel-kit.service";
import { UploadObsService } from "src/app/shared/services/upload-obs.service";
import AppShareHelper from "src/app/shared/utils/helper";
import Swal from "sweetalert2";
import { isRichTextEmpty } from "../shared/rich-text.utils";

interface CourseModel {
  name: string;
  description: string;
  banner?: string;
  courseChapters?: any[];
  id?: number;
  digitalMemoryLogId: number;
  fileType?: boolean;
  link: string;
  priceAmount?: number;
  price?: number;
  currency?: string;
  collapsed?: boolean;
  bannerUploadKey?: string;
}

@Component({
  selector: "app-tab-courses",
  templateUrl: "./tab-courses.component.html",
  styleUrls: ["./tab-courses.component.scss"],
})
export class TabCoursesComponent implements OnInit, OnChanges {
  @Input() model: any = {};
  @Input() formValid: boolean = true;
  @Input() submitted: boolean = false;
  @Input() uploadProgress: { [key: string]: number } = {};
  @Input() uploadStates: { [key: string]: boolean } = {};
  @Input() digitalLogId: any;

  // Course data from parent component
  @Input() courseList: CourseModel[] = [];
  @Input() allCoursesExpanded: boolean = false;
  @Input() courseChanges: boolean[] = [];
  @Input() originalCourses: CourseModel[] = [];

  @Output() modelChange = new EventEmitter<any>();
  @Output() autoCheckEvent = new EventEmitter<string>();
  @Output() courseListChange = new EventEmitter<CourseModel[]>();
  @Output() allCoursesExpandedChange = new EventEmitter<boolean>();
  @Output() courseChangesChange = new EventEmitter<boolean[]>();
  @Output() originalCoursesChange = new EventEmitter<CourseModel[]>();

  // Remove internal course data management (now managed by parent)
  // courseList: CourseModel[] = [];
  // allCoursesExpanded: boolean = false;
  // courseChanges: boolean[] = [];
  // originalCourses: CourseModel[] = [];

  constructor(
    private toastr: ToastrService,
    private uploadService: UploadObsService,
    private travelKitService: TravelKitService,
    private heicConverter: HeicConverterService
  ) {}
  private isFileSizeValid(file: File): boolean {
    const maxSize = 50 * 1024 * 1024; // 50MB
    return file.size <= maxSize;
  }
  ngOnInit() {
    this.initializeCourseData();
    if (this.model.courses && this.model.courses.length > 0) {
      this.model.courses.forEach(section => {
        section.collapsed = false;
        if (section.subSections) {
          section.subSections.forEach(subsection => {
            subsection.collapsed = false;
          });
        }
      });
    }
    this.allCoursesExpanded = true;
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["model"] && changes["model"].currentValue) {
      this.initializeCourseData();
    }
  }

  private initializeCourseData() {
    // If parent has course data, use it; otherwise initialize from model
    if (this.courseList && this.courseList.length > 0) {
      // Use existing course data from parent
      this.courseChanges = this.courseChanges || new Array(this.courseList.length).fill(false);
      this.originalCourses = this.originalCourses || AppShareHelper.deepClone(this.courseList);
    } else {
      // Initialize from model or create default course
      const newCourseList =
        this.model.courses && this.model.courses.length > 0
          ? this.model.courses
          : [
              {
                name: "",
                description: "",
                banner: "",
                courseChapters: [],
                digitalMemoryLogId: this.model.id || this.digitalLogId,
                link: "",
                price: null,
                currency: "",
                collapsed: false,
              },
            ];

      // Emit changes to parent
      this.courseListChange.emit(newCourseList);
      this.courseChangesChange.emit(new Array(newCourseList.length).fill(false));
      this.originalCoursesChange.emit(AppShareHelper.deepClone(newCourseList));
    }

    if (this.courseList && this.courseList.length > 0) {
      this.courseList.forEach(course => {
        if (course.collapsed === undefined || course.collapsed === null) {
          course.collapsed = false;
        }
      });
      this.courseListChange.emit([...this.courseList]);
    }
  }

  // Upload progress tracking
  generateUploadKey(prefix: string, fileName: string, uniqueId?: string): string {
    const timestamp = Date.now();
    const random = Math.random().toString(36).substring(2, 15);
    const cleanFileName = fileName.replace(/[^a-zA-Z0-9.]/g, "_");
    return `${prefix}_${cleanFileName}_${uniqueId || ""}_${timestamp}_${random}`;
  }

  setUploadProgress(key: string, progress: number): void {}

  setUploadState(key: string, isUploading: boolean): void {
    if (!key) return;
    this.uploadStates[key] = isUploading;
  }

  isUploadInProgress(key: string): boolean {
    if (!key) return false;
    return this.uploadStates[key] || false;
  }

  getProgressByKey(key: string): number {
    if (!key) return 0;
    return this.uploadProgress[key] || 0;
  }

  cleanupUpload(key: string): void {
    if (!key) return;
    delete this.uploadProgress[key];
    delete this.uploadStates[key];
  }

  addCourse() {
    // Ensure courseList is initialized
    const currentCourseList = Array.isArray(this.courseList) ? this.courseList : [];

    if (currentCourseList.length < 5) {
      if (!this.model.displayCourses) {
        this.model.displayCourses = true;
      }

      const newCourse: CourseModel = {
        name: "",
        description: "",
        banner: "",
        courseChapters: [],
        digitalMemoryLogId: this.model.id || this.digitalLogId,
        link: "",
        price: null,
        currency: "",
        collapsed: false,
      };

      // Ensure arrays are properly initialized
      const currentCourseChanges = Array.isArray(this.courseChanges) ? this.courseChanges : [];
      const currentOriginalCourses = Array.isArray(this.originalCourses) ? this.originalCourses : [];

      const updatedCourseList = [...currentCourseList, newCourse];
      const updatedCourseChanges = [...currentCourseChanges, false];
      const updatedOriginalCourses = [...currentOriginalCourses, AppShareHelper.deepClone(newCourse)];

      // Emit changes to parent
      this.courseListChange.emit(updatedCourseList);
      this.courseChangesChange.emit(updatedCourseChanges);
      this.originalCoursesChange.emit(updatedOriginalCourses);

      // Set all courses expanded
      updatedCourseList.forEach(course => {
        course.collapsed = false;
      });
      this.allCoursesExpandedChange.emit(true);

      this.modelChange.emit(this.model);

      this.toastr.success("New course added successfully");
    } else {
      this.toastr.error("The maximum number of courses is 5");
    }
  }

  deleteCourse(index: number, course: CourseModel) {
    Swal.fire({
      title: "Are you sure?",
      text: `This will delete Course ${index + 1} and all its content.`,
      type: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete it!",
      cancelButtonText: "Cancel",
    }).then(result => {
      if (result.value) {
        if (course.id && course.id > 0) {
          // Course exists in database, try to delete from API
          this.travelKitService
            .deleteCourse(course.id)
            .toPromise()
            .then(res => {
              this.toastr.success("Course deleted successfully");
              this.removeCourseFromArrays(index);
            })
            .catch(error => {
              console.error("Delete error:", error);
              // If delete failed, still remove from local arrays if it's a 404 (not found)
              if (error.status === 404) {
                this.toastr.warning("Course was already deleted, removing from list");
                this.removeCourseFromArrays(index);
              }
            });
        } else {
          // Course doesn't exist in database yet, just remove from local arrays
          this.removeCourseFromArrays(index);
          this.toastr.success("Course deleted successfully");
        }
      }
    });
  }

  private removeCourseFromArrays(index: number) {
    const updatedCourseList = Array.isArray(this.courseList) ? [...this.courseList] : [];
    const updatedCourseChanges = Array.isArray(this.courseChanges) ? [...this.courseChanges] : [];
    const updatedOriginalCourses = Array.isArray(this.originalCourses) ? [...this.originalCourses] : [];

    if (index >= 0 && index < updatedCourseList.length) {
      updatedCourseList.splice(index, 1);
    }
    if (index >= 0 && index < updatedCourseChanges.length) {
      updatedCourseChanges.splice(index, 1);
    }
    if (index >= 0 && index < updatedOriginalCourses.length) {
      updatedOriginalCourses.splice(index, 1);
    }

    // Always maintain at least 1 course
    if (updatedCourseList.length === 0) {
      const defaultCourse: CourseModel = {
        name: "",
        description: "",
        banner: "",
        courseChapters: [],
        digitalMemoryLogId: this.model.id || this.digitalLogId,
        link: "",
        price: null,
        currency: "",
        collapsed: false,
      };
      updatedCourseList.push(defaultCourse);
      updatedCourseChanges.push(false);
      updatedOriginalCourses.push(AppShareHelper.deepClone(defaultCourse));
    }

    // Set all courses expanded
    updatedCourseList.forEach(c => (c.collapsed = false));

    // Emit changes to parent
    this.courseListChange.emit(updatedCourseList);
    this.courseChangesChange.emit(updatedCourseChanges);
    this.originalCoursesChange.emit(updatedOriginalCourses);
    this.allCoursesExpandedChange.emit(true);
  }

  async uploadCourseBanner(files: File[], courseIndex: number) {
    if (!files || files.length === 0) return;

    let file = files[0];
    const acceptedImageTypes = [
      "image/jpeg",
      "image/jpg",
      "image/png",
      "image/gif",
      "image/webp",
      "image/heic",
      "image/heif",
    ];
    const isHeicByExtension = /\.(heic|heif)$/i.test(file.name);
    const isValidType =
      acceptedImageTypes.includes(file.type.toLowerCase() || "") ||
      (isHeicByExtension && (!file.type || file.type === "application/octet-stream"));
    if (!isValidType) {
      this.toastr.error(`File ${file.name} format not supported. Accepted formats: JPEG, JPG, PNG, GIF, WEBP, HEIC`);
      return;
    }
    if (!this.isFileSizeValid(file)) {
      this.toastr.error("Max file size exceeds 50MB", "Error");
      return;
    }

    if (!this.courseList || courseIndex >= this.courseList.length || courseIndex < 0) {
      this.toastr.error("Invalid course index");
      return;
    }

    file = await this.heicConverter.convertToJpegIfNeeded(file);

    const uploadKey = this.generateUploadKey("course_banner", file.name, `course_${courseIndex}`);
    this.courseList[courseIndex].bannerUploadKey = uploadKey;
    this.setUploadState(uploadKey, true);
    this.uploadService.uploadFileToS3("digital/videos", file).subscribe(
      (response: any) => {
        if (response && response.type === "progress") {
          this.setUploadProgress(uploadKey, response.percent);
          return;
        }

        if (response && response.type === "complete") {
          this.setUploadState(uploadKey, false);
          this.courseList[courseIndex].banner = response.url;
          this.courseList[courseIndex].bannerUploadKey = null;

          this.toastr.success("Banner uploaded successfully");
          this.onCourseChange(courseIndex);
          this.courseListChange.emit([...this.courseList]);
          this.cleanupUpload(uploadKey);
        }
      },
      error => {
        this.setUploadState(uploadKey, false);
        this.courseList[courseIndex].bannerUploadKey = null;
        this.toastr.error(`Failed to upload ${file.name}: ${error.message || error}`);
        console.error("Upload error:", error);
        this.cleanupUpload(uploadKey);
      }
    );
  }

  removeCourseBanner(courseIndex: number) {
    this.courseList[courseIndex].banner = "";
    this.onCourseChange(courseIndex);
    this.courseListChange.emit([...this.courseList]);
  }

  onCourseChange(index: number) {
    if (!Array.isArray(this.courseChanges) || index < 0 || index >= this.courseChanges.length) {
      return;
    }

    const updatedCourseChanges = [...this.courseChanges];
    updatedCourseChanges[index] = true;
    this.courseChangesChange.emit(updatedCourseChanges);
  }

  toggleCourseCollapse(course: CourseModel) {
    course.collapsed = !course.collapsed;
    this.updateExpandAllCoursesState();
    // Emit updated course list
    this.courseListChange.emit([...this.courseList]);
  }

  updateExpandAllCoursesState() {
    const allExpanded = this.courseList.length > 0 && this.courseList.every((course: CourseModel) => !course.collapsed);
    this.allCoursesExpandedChange.emit(allExpanded);
  }

  toggleExpandAllCourses() {
    const newExpandedState = !this.allCoursesExpanded;
    this.courseList.forEach((course: CourseModel) => {
      course.collapsed = !newExpandedState;
    });
    this.allCoursesExpandedChange.emit(newExpandedState);
    this.courseListChange.emit([...this.courseList]);
  }

  isValidUrl(url: string): boolean {
    if (!url) return true;
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  trackByIndex(index: number, item: any): any {
    return index;
  }

  autoCheck(field: string) {
    this.autoCheckEvent.emit(field);
  }

  validateCourses(): boolean {
    if (!this.model.displayCourses) {
      return true; // If courses are not displayed, no validation needed
    }

    // When displayCourses = true, we need to validate all courses
    if (!this.courseList || this.courseList.length === 0) {
      this.toastr.error("Please complete all required fields for courses");
      return false;
    }

    // When courses are displayed, ALL courses must have ALL required fields
    for (let i = 0; i < this.courseList.length; i++) {
      const course = this.courseList[i];

      // All fields are required when displayCourses = true
      if (!course.name || course.name.trim().length === 0) {
        this.toastr.error("Please complete all required fields for courses");
        return false;
      }

      if (isRichTextEmpty(course.description)) {
        this.toastr.error("Please complete all required fields for courses");
        return false;
      }

      if (!course.link || course.link.trim().length === 0) {
        this.toastr.error("Please complete all required fields for courses");
        return false;
      }

      // Validate URL format
      if (!this.isValidUrl(course.link)) {
        this.toastr.error(`Course ${i + 1}: Please enter a valid URL`);
        return false;
      }

      // Validate price if provided
      if (course.price !== null && course.price !== undefined && course.price > 0) {
        if (!course.currency || course.currency.trim().length === 0) {
          this.toastr.error("Please complete all required fields for courses");
          return false;
        }
      }
    }

    return true;
  }

  saveCourses(): Promise<void> {
    return new Promise((resolve, reject) => {
      const sectionPromises = [];

      // Save changed courses
      if (this.courseChanges && this.courseList) {
        // Check if we have any valid courses to save
        const validCourses = this.courseList.filter(course => course.name && course.name.trim().length > 0);

        // If courseList.length = 1 and it's empty, skip all course saving
        if (this.courseList.length === 1 && validCourses.length === 0) {
          console.log("Skipping course save: only default empty course exists");
          resolve();
          return;
        }

        // Only process courses that have been changed AND have valid content
        this.courseChanges.forEach((hasChanged, index) => {
          if (hasChanged && this.courseList[index]) {
            const course = this.courseList[index];

            // Validate required fields - skip if empty
            if (!course.name || !course.name.trim()) {
              console.warn(`Course ${index + 1}: Name is required, skipping save`);
              return;
            }

            const courseData = {
              name: course.name.trim(),
              description: course.description || "",
              banner: course.banner || "",
              digitalMemoryLogId: this.digitalLogId,
              fileType: course.fileType || false,
              link: course.link || "",
              priceAmount: course.priceAmount || 0,
              price: course.price || 0,
              currency: course.currency || "",
            };

            if (course.id && course.id > 0) {
              // Update existing course
              sectionPromises.push(this.travelKitService.updateCourse(courseData, course.id).toPromise());
            } else {
              // Create new course only if it doesn't have pending creation
              if (!course.id || course.id <= 0) {
                sectionPromises.push(
                  this.travelKitService
                    .createCourse(courseData)
                    .toPromise()
                    .then((response: any) => {
                      if (response && response.id) {
                        course.id = response.id;
                      }
                      return response;
                    })
                );
              }
            }
          }
        });
      }

      if (sectionPromises.length > 0) {
        Promise.all(sectionPromises)
          .then(results => {
            // Reset course changes after successful save
            const resetCourseChanges = new Array(this.courseList.length).fill(false);
            const resetOriginalCourses = AppShareHelper.deepClone(this.courseList);

            this.courseChangesChange.emit(resetCourseChanges);
            this.originalCoursesChange.emit(resetOriginalCourses);

            resolve();
          })
          .catch(error => {
            console.error("Error processing courses:", error);
            this.toastr.error("Failed to save some courses");
            reject(error);
          });
      } else {
        resolve();
      }
    });
  }
}
